import { Brain, Dna } from 'lucide-react';
import { motion } from 'motion/react';

import { useTranslation } from '../i18n/hooks';

const specialtyIcons = [
  { icon: '/images/icons/cancer-ribbon-icon.png', color: '#2ab25e' },
  { icon: '/images/icons/heart.svg', color: '#dc2626' },
  { icon: Brain, color: '#7c3aed' },
  { icon: '/images/icons/organ-transplant.svg', color: '#0891b2' },
  { icon: '/images/icons/rare-disease.svg', color: '#ea580c' },
  { icon: Dna, color: '#0d9488' },
];

export default function MedicalSpecialtiesSection() {
  const t = useTranslation();
  return (
    <motion.section
      id="specialties"
      className="bg-gray-50 px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-32"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <div className="mx-auto max-w-6xl">
        {/* Section Header */}
        <motion.div
          className="mb-12 text-center"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t.specialties.title}
          </motion.h2>
          <motion.p
            className="mx-auto max-w-2xl text-base text-gray-600 sm:text-lg"
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t.specialties.disclaimer}
          </motion.p>
        </motion.div>

        {/* Specialties Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.specialties.items.map((specialty, index) => {
            const iconData = specialtyIcons[index];
            return (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm transition-shadow hover:shadow-lg"
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{
                  delay: 0.1 * index,
                  duration: 0.6,
                  ease: 'easeOut',
                }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                {/* Colored top border */}
                <div
                  className="absolute top-0 right-0 left-0 h-1"
                  style={{ backgroundColor: iconData.color }}
                />

                {/* Icon and Title */}
                <div className="mb-4 flex items-center gap-3">
                  <motion.div
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${iconData.color}15` }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{
                      delay: 0.2 + index * 0.1,
                      duration: 0.3,
                      type: 'spring',
                    }}
                    viewport={{ once: true }}
                  >
                    {typeof iconData.icon === 'string' ? (
                      <img
                        src={iconData.icon}
                        alt={specialty.title}
                        className="h-6 w-6"
                        style={{ filter: `hue-rotate(0deg)` }}
                      />
                    ) : (
                      <iconData.icon className="h-6 w-6" />
                    )}
                  </motion.div>

                  <h3 className="text-lg font-bold text-gray-900">
                    {specialty.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-sm leading-relaxed text-gray-600">
                  {specialty.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}
